import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/modal'
import { Button, Textarea } from '@chakra-ui/react'
import { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useFetch, { CachePolicies } from 'use-http'
import useToast from '../../hooks/useToast'
import { UserContext } from '../../providers'

interface Props {
  isOpen: boolean
  onClose(): void
}

const SupportRequest = ({ isOpen, onClose }: Props): React.ReactElement => {
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) =>
    void setText(e.target.value)

  const { request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })
  const toast = useToast()
  const { user } = useContext(UserContext)
  const { t } = useTranslation()
  const initialText = t(
    'referral_support_initial_text',
    `Hi\n\nI wanted to know more about ImageKit.io's referral program. 
    How do I earn the $20 credit?`,
  )
  const [text, setText] = useState(initialText)
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {t('referral_support_request_heading', 'Referral Support Request')}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody spacing={10}>
          {t(
            'referral_support_request_intro',
            `Let us know if you have any query regarding the refferal program and 
            we will be glad to answer it for you.\nAll follow-up conversations will be sent 
            to your email.`,
          )}
          <Textarea
            mt={10}
            value={text}
            onChange={handleInputChange}
            placeholder={t(
              'placeholder_your_message_here',
              'Your message here',
            )}
            size="sm"
          />
        </ModalBody>
        {/* todo: check if it is correct to pass styles here */}
        <ModalFooter borderTop="1px solid #e7eaf0" style={{ gap: '1em' }}>
          <Button
            colorScheme="appBlue"
            onClick={async () => {
              if (!text || text.trim() === '') {
                toast({
                  description: t(
                    'eror_please_provide_complete_info',
                    'Please provide complete information to continue.',
                  ),
                  status: 'error',
                  isClosable: true,
                  duration: 5000,
                  position: 'top-right',
                  variant: 'outline',
                })
                return
              }

              if (user?.clientNumber) {
                await request.post(`/api/email/${user.clientNumber}/send`, {
                  to: 'support@imagekit.io',
                  subject: t(
                    'referral_support_request_subject',
                    'Referral Support Request',
                  ),
                  text: text
                    .replace(/&/gu, '&amp;')
                    .replace(/</gu, '&lt;')
                    .replace(/"/gu, '&quot;'),
                })

                if (request.error) {
                  toast({
                    description: t(
                      'eror_message_could_not_be_sent',
                      'Message could not be sent.',
                    ),

                    status: 'error',
                    isClosable: true,
                    duration: 5000,
                    position: 'top-right',
                    variant: 'outline',
                  })
                } else {
                  toast({
                    title: t(
                      'success_message_sent_title',
                      'We have got your message!',
                    ),
                    description: t(
                      'success_message_sent_description',
                      'Someone from our team will contact you shortly',
                    ),
                    status: 'success',
                    isClosable: true,
                    duration: 5000,
                    position: 'top-right',
                    variant: 'outline',
                  })
                }
              }
            }}
          >
            {t('send', 'Send')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default SupportRequest
